import { useState } from "react";
import Popover from "@mui/material/Popover";
import Paper from "@mui/material/Paper";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import { useTenantTranslation } from "hooks/formatters";
import { PermissionsEditor } from "components/profileSettings/PermissionsEditor";
import { useAdminMode } from "components/profileSettings/useAdminMode";
import { Box, Stack, Tooltip } from "@mui/material";
import { TitledTabs } from "components/common/titledTabs";
import { SettingsMenuButton } from "../SettingsMenuButton";
import { ImpersonateCrane } from "./CraneImpersonation";

type AdminSettingsTab = "permissions" | "impersonation";

interface AdminSettingsMenuProps {
  isVisible: boolean;
}

export const AdminSettingsMenu = ({ isVisible }: AdminSettingsMenuProps) => {
  const [adminMenuAnchor, setAdminMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [selectedTab, setSelectedTab] =
    useState<AdminSettingsTab>("permissions");
  const { t } = useTenantTranslation();
  const { isAdminPaused, isSavingAdminMode, changeAdminMode } = useAdminMode();

  const handleAdminMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAdminMenuAnchor(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminMenuAnchor(null);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <SettingsMenuButton onClick={handleAdminMenuClick}>
        <AdminPanelSettingsRoundedIcon
          sx={{
            color: "white",
            fontSize: "1.25rem",
          }}
        />
      </SettingsMenuButton>

      <Popover
        open={Boolean(adminMenuAnchor)}
        anchorEl={adminMenuAnchor}
        onClose={handleAdminMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{ mt: 1 }}
        slotProps={{
          paper: {
            sx: {
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              boxShadow: "none",
              width: isAdminPaused ? "auto" : 1040,
              maxWidth: "calc(100vw - 32px)",
              maxHeight: "calc(100% - 72px)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            },
          },
        }}
      >
        <Paper
          sx={{
            ...(isAdminPaused ? { py: 1, pl: 1, pr: 2 } : { p: 3, pb: 0 }),
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            minHeight: 0,
          }}
          elevation={0}
        >
          <Stack spacing={2} sx={{ flex: "1 1 auto", minHeight: 0 }}>
            <Tooltip title={t("pauseAdminTooltip")} describeChild>
              <FormControlLabel
                control={
                  <Switch
                    onChange={(_, checked) => {
                      changeAdminMode(checked ? "paused" : "active");
                    }}
                    checked={isAdminPaused}
                    disabled={isSavingAdminMode}
                  />
                }
                label={t("pauseAdmin")}
                sx={{ m: 0, flexShrink: 0, alignSelf: "flex-start" }}
              />
            </Tooltip>
            {isAdminPaused ? null : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: "1 1 auto",
                  minHeight: 0,
                  "& > *": { minHeight: 0 },
                }}
              >
                <TitledTabs
                  tabSpecs={[
                    {
                      title: t("permissionsEditor"),
                      key: "permissions",
                      content: <PermissionsEditor />,
                    },
                    {
                      title: t("craneDriverImpersonation"),
                      key: "impersonation",
                      content: <ImpersonateCrane />,
                    },
                  ]}
                  selectedTabKey={selectedTab}
                  onChange={setSelectedTab}
                  containerSx={{ pt: 2, minHeight: 0 }}
                />
              </Box>
            )}
          </Stack>
        </Paper>
      </Popover>
    </>
  );
};
